/**
 * @file Toolbar.js
 * @description SchedulerProのツールバーコンポーネント
 * @module Toolbar
 * @param {string} title - ツールバーのタイトル
 * @param {Number} fiscalYear - 現在の年度。
 * @param {string} linkButtonName - 別の配乗表を開くリンクボタンの名前。
 * @param {string} linkURL - 別の配乗表を開くリンクのURL。
 * @param {Object} [eventHandlers] - オプションのイベントハンドラーオブジェクト。
 * @param {Function} [eventHandlers.onFiscalYearChange] - 年度変更時に呼び出される関数。
 * @param {Function} [eventHandlers.onReloadClicked] - 更新ボタンがクリックされたときに呼び出される関数。
 * @param {Function} [eventHandlers.onScaleChange] - 表示単位が変更されたときに呼び出される関数。
 * @returns {Object} - ツールバーの構築関数を含むオブジェクト。
 * @example
 * const toolbar = Toolbar("配乗表", 2025, "別の配乗表を開く", "https://example.com/another-schedule", {
 * onFiscalYearChange: (newYear) => console.log(`年度が ${newYear} に変更されました`),
 * onScaleChange: (scale) => console.log(`表示単位が ${scale} に変更されました`),
 * });
 * // ツールバーを構築する
 * const toolbarConfig = toolbar.build();
 */
export default (title, fiscalYear, linkButtonName, linkURL, eventHandlers) => {
  const _title = title;
  const _linkButtonName = linkButtonName;
  const _linkURL = linkURL;
  const _eventHandlers = eventHandlers || {};
  let _fiscalYear = fiscalYear;

  const build = () => {
    return [
      {
        type: "label",
        text: _title,
        style: "font-weight: bold; color: #000000;",
      },
      {
        type: "button",
        icon: "b-icon b-fa-caret-left",
        onClick: async ({ source }) => {
          _fiscalYear--;
          if (_eventHandlers.onFiscalYearChange) {
            // 年度変更イベントを発火
            await _eventHandlers.onFiscalYearChange(_fiscalYear);
          }
          // 隣のピッカーを取得して、選択した年度を設定
          const picker = source.nextSibling;
          picker.value = _fiscalYear;
        },
      },
      {
        type: "displayfield",
        ref: "yearLabel",
        value: _fiscalYear,
        cls: "year-label",
      },
      {
        type: "button",
        icon: "b-icon b-fa-caret-right",
        onClick: async ({ source }) => {
          _fiscalYear++;
          if (_eventHandlers.onFiscalYearChange) {
            // 年度変更イベントを発火
            await _eventHandlers.onFiscalYearChange(_fiscalYear);
          }
          // 隣のピッカーを取得して、選択した年度を設定
          const picker = source.previousSibling;
          picker.value = _fiscalYear;
        },
      },
      {
        type: "button",
        text: "更新",
        onClick: async () => {
          if (_eventHandlers.onReloadClicked) {
            // データを再読み込み
            await _eventHandlers.onReloadClicked();
          }
        },
      },
      {
        type: "combo",
        label: "表示単位",
        width: 140,
        align: "center",
        cls: "scale-text-combo",
        editable: false,
        items: [
          { text: "日", value: "day" },
          { text: "週", value: "week" },
          { text: "月", value: "month" },
        ],
        value: "day",
        onAction: ({ value }) => {
          if (_eventHandlers.onScaleChange) {
            _eventHandlers.onScaleChange(value);
          }
        },
      },
      "->",
      {
        type: "button",
        text: _linkButtonName,
        onClick: () => {
          window.open(_linkURL, "_blank");
        },
      },
    ];
  };

  return {
    build,
  };
};
